import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
    Container, Box, Typography, Paper, Grid, Avatar, Button, Chip,
    CircularProgress, Alert, Divider, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Tooltip, LinearProgress, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PageAnimation from '../components/animations/PageAnimation';
import SEO from '../components/SEO';
import { customerStatusLabel, isInvoiceFinalized } from '../utils/orderStatus';

/* ── colour tokens ── */
const GREEN = '#75a84b';
const GREEN_LIGHT = '#e6f2d9';
const GREEN_DARK = '#5c8d36';
const GREY_BG = '#fcfdfa';
const CARD_SHADOW = '0 4px 20px rgba(0,0,0,0.06)';

/* ── month helpers ── */
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

/* ── status helpers (keyed by lowercase internal status) ── */
const statusColor = {
    pending: { bg: '#fff3e0', fg: '#e65100', icon: <PendingIcon sx={{ fontSize: 16 }} /> },
    printready: { bg: '#e3f2fd', fg: '#1565c0', icon: <PendingIcon sx={{ fontSize: 16 }} /> },
    processing: { bg: '#ede7f6', fg: '#4527a0', icon: <LocalShippingIcon sx={{ fontSize: 16 }} /> },
    completed: { bg: '#e8f5e9', fg: '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    cancelled: { bg: '#ffebee', fg: '#c62828', icon: null },
};

const StatusChip = ({ status }) => {
    const s = statusColor[String(status || '').toLowerCase()] || { bg: '#f5f5f5', fg: '#616161' };
    return (
        <Chip
            size="small"
            label={customerStatusLabel(status)}
            icon={s.icon}
            sx={{
                bgcolor: s.bg, color: s.fg, fontWeight: 600,
                textTransform: 'capitalize', fontSize: '0.75rem',
                '& .MuiChip-icon': { color: s.fg }
            }}
        />
    );
};

/* ── overview stat mini-card ── */
const StatCard = ({ icon, label, value, color = GREEN }) => (
    <Paper elevation={0} sx={{
        p: 2.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2,
        border: '1px solid #e8e8e8', transition: 'box-shadow .2s',
        '&:hover': { boxShadow: CARD_SHADOW }
    }}>
        <Avatar sx={{ bgcolor: `${color}18`, color, width: 48, height: 48 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>{value}</Typography>
        </Box>
    </Paper>
);

/* ═══════════════════════════════════════════════ */

export default function ParentDashboardPage() {
    const navigate = useNavigate();
    const now = new Date();

    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(null);
    const [error, setError] = useState('');

    const [parentGroup, setParentGroup] = useState('');
    const [children, setChildren] = useState([]);
    const [currentEmail, setCurrentEmail] = useState('');

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    /* ── month/year selector ── */
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-indexed

    /* ── tab: 'accounts' | 'admin' ── */
    const [activeTab, setActiveTab] = useState('accounts');

    /* ── client-side access guard ── */
    useEffect(() => {
        const isParent = localStorage.getItem('isParent') === 'true';
        const switchedByParent = localStorage.getItem('switchedByParent') === 'true';
        if (!isParent && !switchedByParent) {
            navigate('/browse', { replace: true });
        }
    }, [navigate]);

    /* ── fetch children ── */
    const fetchChildren = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/parent-group/children');
            setParentGroup(res.data.parent_group);
            setChildren(res.data.children);
            setCurrentEmail(res.data.current_email);
        } catch (err) {
            const msg = err.response?.data?.msg || 'Failed to load group accounts.';
            setError(msg);
            if (err.response?.status === 400 || err.response?.status === 403) {
                navigate('/browse', { replace: true });
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    /* ── fetch admin summary (with month/year) ── */
    const fetchSummary = useCallback(async (yr, mo) => {
        setSummaryLoading(true);
        try {
            const res = await api.get(`/parent-group/admin-summary?year=${yr}&month=${mo}`);
            setSummary(res.data);
        } catch {
            // silent
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchChildren();
    }, [fetchChildren]);

    // Fetch summary whenever month/year or tab changes
    useEffect(() => {
        if (activeTab === 'admin') {
            fetchSummary(selectedYear, selectedMonth);
        }
    }, [activeTab, selectedYear, selectedMonth, fetchSummary]);

    /* ── switch account ── */
    const handleSwitch = async (targetEmail) => {
        if (targetEmail === currentEmail) {
            navigate('/browse');
            return;
        }
        setSwitching(targetEmail);
        try {
            const res = await api.post('/parent-group/switch', { target_email: targetEmail });
            localStorage.setItem('accessToken', res.data.access_token);
            localStorage.setItem('email', res.data.email);
            localStorage.setItem('contactPerson', res.data.contactPerson || '');
            localStorage.setItem('companyName', res.data.companyName || '');
            localStorage.setItem('parentGroup', res.data.parent_group || 'NA');
            // Switching to a child account — not the parent anymore, but was switched by parent
            localStorage.setItem('isParent', 'false');
            localStorage.setItem('switchedByParent', 'true');
            window.dispatchEvent(new Event('storage'));
            navigate('/browse');
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to switch account.');
        } finally {
            setSwitching(null);
        }
    };

    /* ── view invoice ── */
    const handleViewInvoice = (serialNo) => {
        const token = localStorage.getItem('accessToken') || '';
        let baseURL = api.defaults.baseURL || process.env.REACT_APP_API_URL || 'https://mataxexpress.ca/api';
        if (baseURL.startsWith('/')) {
            baseURL = window.location.origin + baseURL;
        }
        const invoiceUrl = `${baseURL}/orders/${serialNo}/download_invoice?token=${token}`;
        window.open(invoiceUrl, '_blank');
    };

    /* ── year options (last 3 years) ── */
    const yearOptions = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
        yearOptions.push(y);
    }

    /* ── Loading state ── */
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: GREEN }} />
            </Box>
        );
    }

    /* ── compute derived stats ── */
    const totalAccounts = children.length;
    const totalOrders = summary?.totals?.orders ?? 0;
    const totalRevenue = summary?.totals?.revenue ?? 0;
    const completedOrders = summary?.totals?.by_status?.Completed?.count ?? 0;

    return (
        <PageAnimation>
            <SEO
                title="Parent Dashboard | Matax Express"
                description="Manage all linked business accounts in your parent group."
                noindex
            />

            {/* ── Hero banner ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)`,
                py: { xs: 4, md: 6 }, px: 2, color: 'white', textAlign: 'center'
            }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', md: '2.2rem' } }}>
                    {parentGroup}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 480, mx: 'auto' }}>
                    Select an account to manage, or view combined insights for all linked locations.
                </Typography>

                {/* Tab pills */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1 }}>
                    {[
                        { key: 'accounts', label: 'Accounts', icon: <StorefrontIcon sx={{ fontSize: 18, mr: .5 }} /> },
                        { key: 'admin', label: 'Admin Overview', icon: <AdminPanelSettingsIcon sx={{ fontSize: 18, mr: .5 }} /> },
                    ].map(tab => (
                        <Button
                            key={tab.key}
                            variant={activeTab === tab.key ? 'contained' : 'outlined'}
                            onClick={() => setActiveTab(tab.key)}
                            startIcon={tab.icon}
                            sx={{
                                borderRadius: '50px', textTransform: 'none', fontWeight: 600,
                                px: 3, py: 1,
                                ...(activeTab === tab.key
                                    ? { bgcolor: 'white', color: GREEN, '&:hover': { bgcolor: '#f5f5f5' } }
                                    : { borderColor: 'rgba(255,255,255,.5)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,.1)' } }
                                )
                            }}
                        >
                            {tab.label}
                        </Button>
                    ))}
                </Box>
            </Box>

            <Box sx={{ bgcolor: '#fcfdfa', minHeight: '60vh' }}>
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

                    {/* ═════════ ACCOUNTS TAB ═════════ */}
                    {/* ═════════ ACCOUNTS TAB ═════════ */}
                    {activeTab === 'accounts' && (() => {
                        // Filter out the parent account (assuming the API uses 'isParent' or 'is_parent' flag to identify it, adjust if your backend uses a different field like checking if child.email === parentEmail)
                        const childAccounts = children.filter(child => !child.isParent && !child.is_parent && child.businessName !== parentGroup);

                        return (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
                                    {childAccounts.length} linked child account{childAccounts.length !== 1 ? 's' : ''}
                                </Typography>

                                <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                                    Tip: Click the Parent Dashboard under Account to switch to another account.
                                </Typography>

                                {/* Added alignItems="stretch" for equal height cards */}
                                <Grid container spacing={3} alignItems="stretch">
                                    {childAccounts.map((child) => {
                                        const isCurrent = child.email === currentEmail;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={child.email} sx={{ display: 'flex' }}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: { xs: 2.5, sm: 3 }, // Optimized padding for better mobile view
                                                        borderRadius: 4,
                                                        border: isCurrent ? `2px solid ${GREEN}` : '1px solid #e8e8e8',
                                                        bgcolor: isCurrent ? GREEN_LIGHT : 'white',
                                                        transition: 'all .25s ease',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                                                            transform: 'translateY(-2px)',
                                                            borderColor: GREEN,
                                                        },
                                                        display: 'flex', flexDirection: 'column',
                                                        height: '100%', width: '100%', minWidth: '320px',// Removed rigid minHeight to adapt smoothly on mobile, relies on flex stretch
                                                        position: 'relative', overflow: 'hidden'
                                                    }}
                                                    onClick={() => handleSwitch(child.email)}
                                                >
                                                    {isCurrent && (
                                                        <Chip
                                                            size="small" label="Current"
                                                            sx={{
                                                                position: 'absolute', top: 12, right: 12,
                                                                bgcolor: GREEN, color: 'white', fontWeight: 700, fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    )}

                                                    <Avatar sx={{
                                                        bgcolor: isCurrent ? GREEN : '#e0e7d1',
                                                        color: isCurrent ? 'white' : GREEN,
                                                        width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, // Scaled down slightly on mobile
                                                        mb: 2, fontSize: '1.4rem', fontWeight: 700
                                                    }}>
                                                        {(child.businessName || child.contactPerson || '?')[0].toUpperCase()}
                                                    </Avatar>

                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: .5, lineHeight: 1.3, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                                        {child.businessName || 'Unnamed Business'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#666', mb: .5 }}>
                                                        <PersonIcon sx={{ fontSize: 14, mr: .5, verticalAlign: 'middle' }} />
                                                        {child.contactPerson || '—'}
                                                    </Typography>
                                                    {child.businessAddress && (
                                                        <Typography
                                                            variant="caption"
                                                            noWrap // Keeps address on one line to prevent unexpected card stretching
                                                            sx={{ color: '#999', display: 'block', mb: 1 }}
                                                        >
                                                            {child.businessAddress}
                                                        </Typography>
                                                    )}

                                                    {/* Pushes the button to the bottom so all buttons align perfectly */}
                                                    <Box sx={{ flexGrow: 1 }} />

                                                    <Button
                                                        fullWidth variant="contained"
                                                        disabled={switching === child.email}
                                                        endIcon={switching === child.email
                                                            ? <CircularProgress size={16} sx={{ color: 'white' }} />
                                                            : <ArrowForwardIcon />
                                                        }
                                                        sx={{
                                                            mt: 2, borderRadius: '50px', textTransform: 'none',
                                                            fontWeight: 600,
                                                            bgcolor: isCurrent ? GREEN : '#f0f0f0',
                                                            color: isCurrent ? 'white' : '#333',
                                                            '&:hover': { bgcolor: isCurrent ? GREEN_DARK : '#e0e0e0' }
                                                        }}
                                                    >
                                                        {isCurrent ? 'Continue' : 'Switch to Account'}
                                                    </Button>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        );
                    })()}

                    {/* ═════════ ADMIN TAB ═════════ */}
                    {activeTab === 'admin' && (
                        <>
                            {/* ── Month / Year selector row ── */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel>Month</InputLabel>
                                        <Select
                                            value={selectedMonth}
                                            label="Month"
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {MONTH_NAMES.map((name, idx) => (
                                                <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={selectedYear}
                                            label="Year"
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {yearOptions.map(y => (
                                                <MenuItem key={y} value={y}>{y}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Tooltip title="Refresh data">
                                    <IconButton onClick={() => fetchSummary(selectedYear, selectedMonth)} sx={{ color: GREEN }}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {summaryLoading ? (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <CircularProgress sx={{ color: GREEN }} />
                                    <Typography variant="body2" sx={{ mt: 2, color: '#888' }}>Loading insights…</Typography>
                                </Box>
                            ) : summary ? (
                                <>
                                    {/* ── KPI row ── */}
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        <Grid item xs={6} md={3}>
                                            <StatCard icon={<StorefrontIcon />} label="Accounts" value={totalAccounts} />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <StatCard icon={<ShoppingCartIcon />} label="Orders" value={totalOrders} />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <StatCard icon={<AttachMoneyIcon />} label="Revenue" value={`$${totalRevenue.toLocaleString()}`} />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <StatCard icon={<CheckCircleIcon />} label="Completed" value={completedOrders} color="#2e7d32" />
                                        </Grid>
                                    </Grid>

                                    {/* ── Per-child breakdown ── */}
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Per-Account Breakdown</Typography>

                                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8e8e8', mb: 4 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: GREY_BG }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Business</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }} align="center">Orders</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }} align="right">Revenue</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }} align="center">Share</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(summary.per_child).map(([email, data]) => {
                                                    const pct = totalRevenue > 0 ? ((data.revenue / totalRevenue) * 100) : 0;
                                                    return (
                                                        <TableRow key={email} hover sx={{ cursor: 'pointer' }} onClick={() => handleSwitch(email)}>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Avatar sx={{ bgcolor: GREEN_LIGHT, color: GREEN, width: 32, height: 32, fontSize: '.85rem', fontWeight: 700 }}>
                                                                        {(data.businessName || '?')[0]}
                                                                    </Avatar>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.businessName}</Typography>
                                                                        <Typography variant="caption" color="text.secondary">{data.contactPerson}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.orders}</Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>${data.revenue.toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ width: 140 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <LinearProgress
                                                                        variant="determinate" value={pct}
                                                                        sx={{
                                                                            flexGrow: 1, height: 8, borderRadius: 4,
                                                                            bgcolor: '#eee',
                                                                            '& .MuiLinearProgress-bar': { bgcolor: GREEN, borderRadius: 4 }
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 32, textAlign: 'right' }}>
                                                                        {pct.toFixed(0)}%
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* ── Monthly orders (all orders for selected month) ── */}
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                        Orders — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                                    </Typography>

                                    {(summary.month_orders || []).length === 0 ? (
                                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e8e8e8', textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">No orders found for this month.</Typography>
                                        </Paper>
                                    ) : (
                                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8e8e8' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: GREY_BG }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>INV #</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }} align="center">Paid</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }} align="center">Invoice</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {summary.month_orders.map((order, idx) => {
                                                        const isInvoiceReady = isInvoiceFinalized(order.status);
                                                        return (
                                                            <TableRow key={idx} hover>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        INV-{String(order.serial_no).padStart(4, '0')}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">{order.businessName}</Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        ${(order.total_amount || 0).toFixed(2)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <StatusChip status={order.status} />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Chip
                                                                        size="small"
                                                                        label={order.isPaid ? 'Paid' : 'Unpaid'}
                                                                        sx={{
                                                                            bgcolor: order.isPaid ? '#e8f5e9' : '#fff3e0',
                                                                            color: order.isPaid ? '#2e7d32' : '#e65100',
                                                                            fontWeight: 600, fontSize: '0.72rem'
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {isInvoiceReady ? (
                                                                        <Tooltip title="View / Print Invoice">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleViewInvoice(order.serial_no)}
                                                                                sx={{ color: GREEN }}
                                                                            >
                                                                                <ReceiptLongIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </>
                            ) : (
                                <Alert severity="info">Could not load admin summary.</Alert>
                            )}
                        </>
                    )}
                </Container>
            </Box>
        </PageAnimation>
    );
}
